import type { OneMedia } from '@prismamedia/one-components';
import type { RawDraftContentState } from 'draft-js';
import type { GetArticleQuery_query_article_Article_tags_ArticleTag_tag_Tag } from '../../../../../../__generated__/queries-web';
import {
  CategoryFragment_Category_tabs_Tab,
  TabType,
} from '../../../../../../__generated__/queries-web';
import { ItemSerialized } from '../../../../../../components/MediaInput/MultimediaPlugin/types';
import type { OmitMetaTypename } from '../../../../../../types/generic';
import { FormError } from '../../../../types/index';
import { DEFAULT_CATEGORY_SECTION_ERROR } from './constants';

export enum CategoryFormSectionContentType {
  ARTICLE = 'article',
  RECIPE = 'recipe',
  SLIDESHOW = 'slideshow',
}

export interface SectionTabTag {
  order?: number;
  tag: { id: string; title: string };
}

export interface SectionTabQualifier {
  order?: number;
  qualifier: { id: string; title: string };
}

/** Tab fields sent when saving a category (avoids Partial<discriminated union> issues). */
export interface CategoryTabSavePayload {
  content: string | null;
  createdAt?: string;
  description: string;
  id: string;
  isOnline: boolean;
  metas: string;
  order: number;
  qualifiers?: SectionTabQualifier[];
  tags?: SectionTabTag[];
  title: string;
  type: TabType;
}

export type Section = (
  | { type: TabType.Board }
  | { type: TabType.People }
  | { type: TabType.Text }
  | { type: TabType.Url }
) &
  OmitMetaTypename<CategoryFragment_Category_tabs_Tab> & {
    qualifiers?: SectionTabQualifier[];
    tags?: SectionTabTag[];
  };

/// CATEGORY SECTION FORM TYPES ////////////////////////////////////////////
export interface CategoryFormSectionBoardContent
  extends Omit<ItemSerialized, 'pluginType'> {
  id: string;
  order: number;
  type: CategoryFormSectionContentType;
}

export interface CategoryFormSectionBoardTag {
  id: string;
  title: string;
}

export interface CategoryFormSectionBoardQualifier {
  id: string;
  title: string;
}

export interface CategoryFormSectionPeopleContent
  extends GetArticleQuery_query_article_Article_tags_ArticleTag_tag_Tag {
  fullName?: string;
  order?: number;
  person?: string;
  id: string;
  isActive?: boolean;
}

export interface CategoryFormSectionTextContent {
  body: RawDraftContentState;
  contentTitle: string;
}

export type CategoryFormSectionUrlContent = [string, OneMedia?];

export type SectionForm<C = void, T = void> = (C & T extends void
  ?
      | {
          content: CategoryFormSectionBoardContent[];
          qualifiers: CategoryFormSectionBoardQualifier[];
          tags: CategoryFormSectionBoardTag[];
          type: TabType.Board;
        }
      | {
          content: CategoryFormSectionPeopleContent[];
          type: TabType.People;
        }
      | {
          content: CategoryFormSectionTextContent;
          type: TabType.Text;
        }
      | {
          content: CategoryFormSectionUrlContent;
          type: TabType.Url;
        }
  : { content: C; type: T }) & {
  createdAt?: string;
  description: string;
  id: string;
  isActive: boolean;
  order: number;
  title: string;
  metas: string;
};

export type SectionErrors = typeof DEFAULT_CATEGORY_SECTION_ERROR & {
  content?: FormError[];
};
